import type { MouseEvent as ReactMouseEvent } from 'react';

import type {
  AreaTag,
  LayoutPoint,
  ViewerEntityId,
} from '../contracts/models';
import {
  calculatePolygonCentroid,
  normalizeLayoutPolygon,
} from '../geometry/polygons';

export interface AreaTagDeleteLayerProps {
  areaTags: readonly AreaTag[];
  visible?: boolean;
  editingAreaTagId?: ViewerEntityId | null;
  getPoints?: (
    areaTag: AreaTag,
  ) => readonly (readonly number[])[];
  getLabelPosition?: (
    areaTag: AreaTag,
  ) => LayoutPoint | null | undefined;
  offsetY?: number;
  onDelete(
    areaTag: AreaTag,
    event: ReactMouseEvent<SVGGElement>,
  ): void;
}

export const AreaTagDeleteLayer = ({
  areaTags,
  visible = true,
  editingAreaTagId = null,
  getPoints = (areaTag) => areaTag.points,
  getLabelPosition = (areaTag) =>
    areaTag.label?.position ?? null,
  offsetY = -26,
  onDelete,
}: AreaTagDeleteLayerProps) => {
  if (!visible) return null;

  return (
    <g className="iv-area-tag-delete-layer">
      {areaTags.map((areaTag) => {
        if (
          editingAreaTagId !== null &&
          String(areaTag.id) !== String(editingAreaTagId)
        ) {
          return null;
        }
        const points = normalizeLayoutPolygon(getPoints(areaTag));
        if (points.length < 3) return null;

        const labelPosition = getLabelPosition(areaTag);
        const centroid = calculatePolygonCentroid(points);
        const x = labelPosition?.[0] ?? centroid.x;
        const y = (labelPosition?.[1] ?? centroid.y) + offsetY;

        return (
          <g
            key={String(areaTag.id)}
            className="iv-area-tag-delete-control"
            style={{ cursor: 'pointer', pointerEvents: 'auto' }}
            onClick={(event) => {
              event.stopPropagation();
              onDelete(areaTag, event);
            }}
          >
            <circle cx={x} cy={y} r="11" fill="#dc2626" />
            <rect
              x={x - 3.5}
              y={y - 1.5}
              width={7}
              height={7}
              rx={1}
              fill="none"
              stroke="#ffffff"
              strokeWidth={1}
              pointerEvents="none"
            />
            <line
              x1={x - 4.5}
              y1={y - 1.5}
              x2={x + 4.5}
              y2={y - 1.5}
              stroke="#ffffff"
              strokeWidth={1}
              pointerEvents="none"
            />
            <line
              x1={x - 2}
              y1={y - 3.5}
              x2={x + 2}
              y2={y - 3.5}
              stroke="#ffffff"
              strokeWidth={1}
              pointerEvents="none"
            />
            <line
              x1={x - 1.2}
              y1={y + 1}
              x2={x - 1.2}
              y2={y + 4.5}
              stroke="#ffffff"
              strokeWidth={0.8}
              pointerEvents="none"
            />
            <line
              x1={x + 1.2}
              y1={y + 1}
              x2={x + 1.2}
              y2={y + 4.5}
              stroke="#ffffff"
              strokeWidth={0.8}
              pointerEvents="none"
            />
          </g>
        );
      })}
    </g>
  );
};
